/**
 * Templates - Generates HTML templates for voice interface
 * 
 * Required from parent (voiceInterface):
 * - config: { language, translations, ... }
 * - isMobile: boolean
 * - t(key): string (translation helper method)
 * - getText(directConfigKey, translationKey): string
 * - getTooltip(key): string
 * 
 * State owned by this module:
 * - None (stateless template generation)
 */

import { configPrefersRtlText, dirForAssistantStreamingText, dirForTranscriptSnippet } from './rtlText.js';

/** Allow only http(s) absolute URLs for idle header avatar images. */
export function sanitizeHeaderAvatarImageUrl(raw) {
  const s = (raw != null && String(raw).trim()) || '';
  if (!s) return '';
  try {
    const u = new URL(s);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return '';
    return u.href;
  } catch {
    return '';
  }
}

export function escapeHtmlAttr(s) {
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

/**
 * Resolve idle header avatar URL from flat + nested voice config (and legacy / API aliases).
 * The idle UI shows the first letter of the agent name only when this returns ''.
 */
export function resolveIdleHeaderAvatarUrl(config) {
  if (!config) return '';
  const v = config.voice || {};
  const candidates = [
    config.headerAvatarImageUrl,
    v.headerAvatarImageUrl,
    config.header_avatar_image_url,
    v.header_avatar_image_url,
    v.avatarImageUrl,
    config.avatarImageUrl,
    v.avatar_image_url,
    config.avatar_image_url
  ];
  for (const raw of candidates) {
    const u = sanitizeHeaderAvatarImageUrl(raw);
    if (u) return u;
  }
  return '';
}

export class Templates {
  constructor(voiceInterface) {
    this.voiceInterface = voiceInterface;
    this.config = voiceInterface.config;
  }

  /**
   * Helper to access translation method from parent
   */
  t(key) {
    return this.voiceInterface.t(key);
  }

  /**
   * Helper to access getText method from parent
   */
  getText(directConfigKey, translationKey) {
    return this.voiceInterface.getText(directConfigKey, translationKey);
  }

  /**
   * Helper to access getTooltip method from parent
   */
  getTooltip(key) {
    return this.voiceInterface.getTooltip(key);
  }

  /**
   * Shared "About" info ("i") button markup. Opens the About dialog
   * (SDK version + last conversation id) wired by TTPChatWidget.
   * @param {string} [extraClass] - extra class(es) for positioning per view
   * @returns {string}
   */
  infoButton(extraClass = '') {
    const cls = extraClass ? `ttp-info-btn ${extraClass}` : 'ttp-info-btn';
    const label = this.t('aboutTitle') || 'About';
    return `<button type="button" class="${cls}" aria-label="${escapeHtmlAttr(label)}" title="${escapeHtmlAttr(label)}">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="9"/>
                <line x1="12" y1="11" x2="12" y2="16"/>
                <circle cx="12" cy="7.7" r="1.05" fill="currentColor" stroke="none"/>
              </svg>
            </button>`;
  }

  /**
   * Shared in-call settings gear button markup. Opens the call-settings dialog
   * (voice speed / response length sliders) wired by TTPChatWidget. Shares the
   * .ttp-info-btn styling class; .ttp-settings-btn is the wiring discriminator.
   * @param {string} [extraClass] - extra class(es) for positioning per view
   * @returns {string}
   */
  settingsButton(extraClass = '') {
    const cls = extraClass
      ? `ttp-info-btn ttp-settings-btn ${extraClass}`
      : 'ttp-info-btn ttp-settings-btn';
    const label = this.t('settingsTitle') || 'Call settings';
    return `<button type="button" class="${cls}" aria-label="${escapeHtmlAttr(label)}" title="${escapeHtmlAttr(label)}">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h.01a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </button>`;
  }

  /**
   * Generate HTML for voice interface.
   * Automatically detects mobile vs desktop based on voiceInterface.isMobile
   * and returns the appropriate template.
   *
   * @returns {string} HTML template string for the voice interface
   */
  generateHTML() {
    // Mobile UI - minimized bar design
    if (this.voiceInterface.isMobile) {
      return this.generateMobileHTML();
    }
    
    // Desktop UI - new dark theme design
    // Prefer explicit agent name over header.title so the pill can use a separate CTA (header.pillTitle + header.title).
    const agentName = (this.config.agentName != null && String(this.config.agentName).trim() !== '')
      ? String(this.config.agentName).trim()
      : 'Sasha';
    const agentRole = this.config.agentRole || 'AI Voice Assistant';
    const headline = this.config.headline || this.config.startCallTitle || 'Hi there 👋';
    const subline = this.config.subline || this.config.startCallSubtitle || 'Ask me anything — I respond instantly<br>in voice or text.';
    const avatarInitial = agentName.charAt(0).toUpperCase();
    const headerAvatarUrl = resolveIdleHeaderAvatarUrl(this.config);
    const idleAvatarInner = headerAvatarUrl
      ? `<img src="${escapeHtmlAttr(headerAvatarUrl)}" alt="" class="ttp-avatar-img" />`
      : avatarInitial;
    const idleAvatarClass = headerAvatarUrl ? 'ttp-avatar ttp-avatar--image' : 'ttp-avatar';
    const textDir = configPrefersRtlText(this.config) ? 'rtl' : 'ltr';
    const transcriptPlaceholder = this.t('transcriptWillAppear') || '';
    const transcriptDir = dirForTranscriptSnippet(transcriptPlaceholder, this.config);
    const voiceInputDir = dirForAssistantStreamingText(this.config);

    return `<div class="voice-interface" id="voiceInterface">
      <!-- Idle View -->
      <div id="voiceIdleState">
        <div class="ttp-hero">
          <div class="ttp-header">
            <div class="ttp-agent">
              <div class="ttp-avatar-wrap">
                <div class="${idleAvatarClass}">${idleAvatarInner}</div>
                <div class="ttp-online-dot"></div>
              </div>
              <div>
                <div class="ttp-agent-name">${agentName}</div>
                <div class="ttp-agent-role">${agentRole}</div>
              </div>
            </div>
            <div class="ttp-header-actions">
              ${this.infoButton()}
              <button type="button" class="ttp-close-btn" id="ttpCloseIdle" aria-label="Close widget">
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M10 2L2 10M2 2l8 8"/>
                </svg>
              </button>
            </div>
          </div>
          <div class="ttp-headline">${headline}</div>
          <div class="ttp-subline">${subline}</div>
        </div>

        <div class="ttp-body">
          <button type="button" class="ttp-btn-primary" id="startCallBtn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${this.config.startCallButtonTextColor || 'white'}" stroke-width="2">
              <path d="M12 2a3 3 0 0 1 3 3v7a3 3 0 0 1-6 0V5a3 3 0 0 1 3-3z"/>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v3M8 22h8"/>
            </svg>
            ${this.getText('startCallButtonText', 'startCall') || 'Start Voice Call'}
          </button>
          ${(this.config.behavior?.mode || 'unified') !== 'voice-only' ? `
          <button type="button" class="ttp-btn-secondary" id="startTextBtn">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            ${this.config.sendMessageText || this.getText('sendMessageText', 'sendMessage') || 'Send a Message'}
          </button>
          ` : ''}
          ${this.config.whatsapp?.number ? `
          <a class="ttp-btn-wa" id="ttpWhatsAppBtn"
             href="https://wa.me/${this.config.whatsapp.number}${this.config.whatsapp.text ? '?text=' + encodeURIComponent(this.config.whatsapp.text) : ''}"
             target="_blank" rel="noopener">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="#25d366">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.655-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            WhatsApp
          </a>
          ` : ''}
        </div>
      </div>

      <!-- Active Call View -->
      <div id="voiceActiveState" style="display: none;">
        <!-- Full voice section (hidden when items/rooms are shown) -->
        <div class="desktop-voice-section" id="desktopVoiceSection">
          <div class="ttp-call-hero">
            <div class="ttp-call-topbar">
              <div class="ttp-rec">
                <div class="ttp-rec-dot"></div>
                <span class="ttp-timer" id="desktopTimerText">00:00</span>
              </div>
              <span class="ttp-call-label">Voice call active</span>
              <div class="ttp-header-actions">
                ${this.settingsButton()}
                ${this.infoButton()}
                <button type="button" class="ttp-close-btn" id="ttpCloseActive" aria-label="Close widget">
                  <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M10 2L2 10M2 2l8 8"/>
                  </svg>
                </button>
              </div>
            </div>

            <div class="ttp-orb-wrap">
              <div class="ttp-orb-rings">
                <div class="ttp-ring ttp-ring-1"></div>
                <div class="ttp-ring ttp-ring-2"></div>
                <div class="ttp-orb" id="desktopMainWaveform">
                  <div class="ttp-wave" id="ttp-wave"></div>
                </div>
              </div>
              <div class="ttp-listen-label" id="desktopStatusText">${this.t('listening') || 'Listening to you...'}</div>
            </div>
          </div>

          <div class="ttp-controls">
            <button type="button" class="ttp-ctrl-btn" id="desktopMuteBtn" title="${this.getTooltip('mute')}" aria-label="Mute microphone">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" class="desktop-mic-icon">
                <path d="M12 1a3 3 0 0 1 3 3v7a3 3 0 0 1-6 0V4a3 3 0 0 1 3-3z"/>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
              </svg>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" class="desktop-mic-off-icon" style="display: none; position: absolute;">
                <path d="M12 1a3 3 0 0 1 3 3v7a3 3 0 0 1-6 0V4a3 3 0 0 1 3-3z" stroke="currentColor" stroke-width="1.8" fill="none"/>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" stroke="currentColor" stroke-width="1.8" fill="none"/>
                <line x1="5" y1="5" x2="19" y2="19" stroke="#ef4444" stroke-width="1.5" stroke-linecap="round" opacity="0.85"/>
              </svg>
            </button>
            <button type="button" class="ttp-ctrl-btn" id="desktopPauseBtn" title="Pause call" aria-label="Pause call">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="desktop-pause-icon">
                <rect x="6" y="4" width="4" height="16" rx="1"/>
                <rect x="14" y="4" width="4" height="16" rx="1"/>
              </svg>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none" class="desktop-play-icon" style="display: none; position: absolute;">
                <polygon points="6,4 20,12 6,20"/>
              </svg>
            </button>
            <button type="button" class="ttp-ctrl-btn" id="desktopSpeakerBtn" title="${this.getTooltip('speaker')}" aria-label="Toggle speaker">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" class="desktop-speaker-icon">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
              </svg>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" class="desktop-speaker-off-icon" style="display: none; position: absolute;">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" stroke="currentColor" stroke-width="1.8" fill="none"/>
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" stroke="currentColor" stroke-width="1.8" fill="none"/>
                <line x1="5" y1="5" x2="19" y2="19" stroke="#ef4444" stroke-width="1.5" stroke-linecap="round" opacity="0.85"/>
              </svg>
            </button>
            <button type="button" class="ttp-end-btn" id="desktopEndCallBtn" title="${this.getTooltip('endCall')}" aria-label="End call">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.99 12 19.79 19.79 0 0 1 1.97 3.4 2 2 0 0 1 3.95 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Compact voice section (shown when items/rooms are visible) -->
        <div class="compact-voice-section" id="compactVoiceSection" style="display: none;">
          <div class="compact-left">
            <div class="compact-avatar" id="desktopAvatarCompact">
              <div class="ttp-orb compact-orb" id="desktopMainWaveformCompact">
                <div class="ttp-wave"></div>
              </div>
            </div>
            <div class="compact-info">
              <div class="compact-timer" id="desktopTimerCompact">
                <span class="ttp-rec-dot"></span>
                <span id="compactTimerText">00:00</span>
              </div>
              <div class="compact-status" id="desktopStatusCompact">
                <span id="compactStatusText">${this.t('listening') || 'Listening...'}</span>
              </div>
            </div>
          </div>
          <div class="compact-controls">
            ${this.settingsButton('ttp-info-btn--onctrl')}
            ${this.infoButton('ttp-info-btn--onctrl')}
            <button type="button" class="compact-control-btn secondary" id="desktopMuteBtnCompact" title="${this.getTooltip('mute')}" aria-label="Mute microphone">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" class="desktop-mic-icon">
                <path d="M12 1a3 3 0 0 1 3 3v7a3 3 0 0 1-6 0V4a3 3 0 0 1 3-3z"/>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
              </svg>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" class="desktop-mic-off-icon" style="display: none; position: absolute;">
                <path d="M12 1a3 3 0 0 1 3 3v7a3 3 0 0 1-6 0V4a3 3 0 0 1 3-3z" stroke="currentColor" stroke-width="1.8" fill="none"/>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" stroke="currentColor" stroke-width="1.8" fill="none"/>
                <line x1="5" y1="5" x2="19" y2="19" stroke="#ef4444" stroke-width="1.5" stroke-linecap="round" opacity="0.85"/>
              </svg>
            </button>
            <button type="button" class="compact-control-btn secondary" id="desktopPauseBtnCompact" title="Pause call" aria-label="Pause call">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="desktop-pause-icon">
                <rect x="6" y="4" width="4" height="16" rx="1"/>
                <rect x="14" y="4" width="4" height="16" rx="1"/>
              </svg>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none" class="desktop-play-icon" style="display: none; position: absolute;">
                <polygon points="6,4 20,12 6,20"/>
              </svg>
            </button>
            <button type="button" class="compact-control-btn secondary" id="desktopSpeakerBtnCompact" title="${this.getTooltip('speaker')}" aria-label="Toggle speaker">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" class="desktop-speaker-icon">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
              </svg>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" class="desktop-speaker-off-icon" style="display: none; position: absolute;">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" stroke="currentColor" stroke-width="1.8" fill="none"/>
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" stroke="currentColor" stroke-width="1.8" fill="none"/>
                <line x1="5" y1="5" x2="19" y2="19" stroke="#ef4444" stroke-width="1.5" stroke-linecap="round" opacity="0.85"/>
              </svg>
            </button>
            <button type="button" class="compact-control-btn danger" id="desktopEndCallBtnCompact" title="${this.getTooltip('endCall')}" aria-label="End call">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="overflow: visible;">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.99 12 19.79 19.79 0 0 1 1.97 3.4 2 2 0 0 1 3.95 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" transform="rotate(135 12 12)"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Transcript -->
        <div class="ttp-transcript desktop-conversation-panel" id="desktopConversationPanel" dir="${textDir}">
          <div class="ttp-transcript-hdr" id="conversationHeader">
            <span class="ttp-live-label">Live</span>
            <span class="ttp-toggle-text" id="conversationToggleText">Show history</span>
            <div class="ttp-dots">
              <div class="ttp-dot"></div>
              <div class="ttp-dot"></div>
              <div class="ttp-dot"></div>
            </div>
          </div>
          <div class="live-transcript-collapsed" id="liveTranscriptCollapsed">
            <div class="ttp-transcript-text" id="liveTextCollapsed" dir="${transcriptDir}">
              ${this.t('transcriptWillAppear') || 'Transcript will appear here...'}<span class="ttp-cursor"></span>
            </div>
          </div>
          <div class="conversation-history" id="conversationHistory">
            <div class="conversation-messages" id="conversationMessages"></div>
            <div class="live-message-row" id="liveMessageRow" style="display: none;">
              <div class="message-bubble" dir="${transcriptDir}">
                <span class="live-badge">Live</span>
                <span id="liveMessageText"></span><span class="ttp-cursor"></span>
              </div>
            </div>
          </div>
        </div>

        <!-- Type row -->
        <div class="ttp-type-row voice-text-input-area">
          <input class="ttp-input" type="text" id="voiceTextInput" dir="${voiceInputDir}" placeholder="${this.getText('inputPlaceholder', 'typeMessage') || 'Or type here…'}" aria-label="Type a message" />
          <button type="button" class="ttp-send-btn" id="voiceSendBtn" aria-label="Send message">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </div>
      </div>
    </div>`;
  }

  /**
   * Generate mobile-specific HTML for minimized voice call UI
   */
  generateMobileHTML() {
    // For mobile, idle state goes in widget, but active state will be appended to body
    const textDir = configPrefersRtlText(this.config) ? 'rtl' : 'ltr';
    const transcriptPlaceholder = this.t('transcriptWillAppear') || '';
    const transcriptDir = dirForTranscriptSnippet(transcriptPlaceholder, this.config);
    const voiceInputDir = dirForAssistantStreamingText(this.config);
    const headerAvatarUrl = resolveIdleHeaderAvatarUrl(this.config);
    const mobileAvatarInner = headerAvatarUrl
      ? `<img src="${escapeHtmlAttr(headerAvatarUrl)}" alt="" class="voice-avatar-img" />`
      : '🤖';
    const mobileAvatarClass = headerAvatarUrl ? 'voice-avatar voice-avatar--image' : 'voice-avatar';
    return `<div class="voice-interface mobile-voice-interface" id="voiceInterface" dir="${textDir}">
      <!-- Before Call State (same as desktop) -->
      <div id="voiceIdleState">
        ${this.infoButton('ttp-info-btn--floating')}
        <div class="${mobileAvatarClass}" id="voiceAvatar">${mobileAvatarInner}</div>
        <div class="voice-status">
          <div class="voice-status-title">${this.getText('startCallTitle', 'clickToStartCall')}</div>
          <div class="voice-status-subtitle">${this.getText('startCallSubtitle', 'realTimeVoice')}</div>
        </div>
        <button type="button" class="start-call-btn" id="startCallBtn">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
          </svg>
          <span>${this.getText('startCallButtonText', 'startCall')}</span>
        </button>
      </div>
      
      <!-- Mobile Active Call State - Hidden placeholder (actual bar will be appended to body) -->
      <div id="voiceActiveState" class="mobile-active-state" style="display: none;">
        <!-- Duration Badge -->
        <div class="mobile-duration-badge" id="mobileDurationBadge">
          <div class="mobile-duration-dot"></div>
          <span id="mobileDurationText">00:00</span>
          ${this.settingsButton('ttp-info-btn--onbadge')}
          ${this.infoButton('ttp-info-btn--onbadge')}
        </div>

        <!-- Main Bar -->
        <div class="mobile-voice-bar">
          <!-- Top Row: Status, Waveform, Controls -->
          <div class="mobile-top-row">
            <!-- Status Indicator -->
            <div class="mobile-status-indicator">
              <div class="mobile-status-dot" id="mobileStatusDot"></div>
              <span class="mobile-status-text" id="mobileStatusText">Listening...</span>
            </div>
            
            <!-- Centered Waveform -->
            <div class="mobile-waveform-center" id="mobileWaveform">
              ${Array(7).fill(0).map((_, i) => 
                `<div class="mobile-waveform-bar" data-index="${i}"></div>`
              ).join('')}
            </div>
            
            <!-- Controls -->
            <div class="mobile-controls">
              <button type="button" class="mobile-control-btn" id="mobileMuteBtn" aria-label="Mute microphone">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="mobile-mic-icon">
                  <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <line x1="12" x2="12" y1="19" y2="22"/>
                </svg>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="mobile-mic-off-icon" style="display: none;">
                  <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <line x1="12" x2="12" y1="19" y2="22"/>
                  <line x1="4" y1="4" x2="20" y2="20" stroke="#ef4444" stroke-width="1.5" opacity="0.85"/>
                </svg>
              </button>
              
              <button type="button" class="mobile-control-btn" id="mobilePauseBtn" aria-label="Pause call">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" class="mobile-pause-icon">
                  <rect x="6" y="4" width="4" height="16" rx="1"/>
                  <rect x="14" y="4" width="4" height="16" rx="1"/>
                </svg>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="#fff" stroke="none" class="mobile-play-icon" style="display: none;">
                  <polygon points="6,4 20,12 6,20"/>
                </svg>
              </button>

              <button type="button" class="mobile-control-btn" id="mobileSpeakerBtn" aria-label="Toggle speaker">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="mobile-speaker-icon">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                </svg>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="mobile-speaker-off-icon" style="display: none;">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                  <line x1="4" y1="4" x2="20" y2="20" stroke="#ef4444" stroke-width="1.5" opacity="0.85"/>
                </svg>
              </button>

              <button type="button" class="mobile-end-call-btn" id="mobileEndCallBtn" aria-label="End call">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91"/>
                  <line x1="22" x2="2" y1="2" y2="22"/>
                </svg>
              </button>
            </div>
          </div>
          
          <!-- Bottom Row: Transcript (expandable) -->
          <button type="button" class="mobile-transcript-row" id="mobileTranscriptRow">
            <p class="mobile-transcript-text" id="mobileTranscriptText" dir="${transcriptDir}">${this.t('transcriptWillAppear')}</p>
            <div class="mobile-transcript-footer">
              <span class="mobile-expand-hint">tap to expand & type</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="opacity: 0.6;">
                <path d="M7 17L17 7M17 7H7M17 7V17"/>
              </svg>
            </div>
          </button>
        </div>
      </div>
      
      <!-- Expanded Transcript Overlay -->
      <div class="mobile-transcript-overlay" id="mobileTranscriptOverlay">
        <div class="mobile-expanded-transcript" id="mobileExpandedTranscript">
          <!-- Header -->
          <div class="mobile-transcript-header">
            <div class="mobile-header-left">
              <div class="mobile-header-status">
                <div class="mobile-header-status-dot"></div>
                <span class="mobile-header-status-text" id="mobileHeaderStatusText">Listening...</span>
                <span class="mobile-header-duration" id="mobileHeaderDuration">00:00</span>
              </div>
              <span class="mobile-transcript-label">CONVERSATION</span>
            </div>
            <button type="button" class="mobile-close-transcript-btn" id="mobileCloseTranscriptBtn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
          
          <!-- Messages Container -->
          <div class="mobile-messages-container" id="mobileMessagesContainer">
            <!-- Messages will be dynamically added here -->
          </div>
          
          <!-- Text Input Area -->
          <div class="mobile-input-area">
            <div class="mobile-input-wrapper">
              <input
                type="text"
                placeholder="Type a message..."
                id="mobileTextInput"
                class="mobile-text-input"
                dir="${voiceInputDir}"
              />
              <button type="button" class="mobile-send-btn" id="mobileSendBtn" disabled>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
                </svg>
              </button>
            </div>
            <p class="mobile-input-hint">🎙️ Voice is still active • Type or speak</p>
          </div>
        </div>
      </div>
    </div>`;
  }
}
